import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import TherapyCard from "../components/TherapyCard";
import { StartingAtPill } from "../components/Pricing";
import Disclaimer from "../components/Disclaimer";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { ArrowLeft } from "lucide-react";

export default function GoalDetail() {
  const { slug } = useParams();
  const [g, setG] = useState(null);
  useEffect(() => { api.get(`/goals/${slug}`).then(({ data }) => setG(data)); }, [slug]);

  if (!g) return <div className="vv-container py-24 text-stone-500">Loading…</div>;

  const priced = (g.therapies || []).filter((t) => !t.hide_pricing && t.display_monthly_price != null);
  const minPrice = priced.length ? Math.min(...priced.map((t) => t.promo_price || t.display_monthly_price)) : null;

  return (
    <div data-testid="goal-detail-page">
      <SEO
        title={`${g.name} — Peptide Therapy Options & Bloodwork`}
        description={`Educational overview of ${g.name.toLowerCase()}: contributing factors, bloodwork considerations, and peptide options a licensed VidaVital Medical provider may evaluate.`}
        path={`/treatment-goals/${g.slug}`}
        breadcrumbs={[
          { name: "Treatment Goals", path: "/treatment-goals" },
          { name: g.name,           path: `/treatment-goals/${g.slug}` },
        ]}
      />
      <section className="vv-hero">
        <div className="vv-container py-14">
          <Breadcrumbs items={[
            { name: "Treatment Goals", path: "/treatment-goals" },
            { name: g.name,           path: `/treatment-goals/${g.slug}` },
          ]} />
          <Link to="/treatment-goals" className="text-sm text-stone-500 inline-flex items-center gap-1"><ArrowLeft size={14} /> Treatment Goals</Link>
          <h1 className="font-display text-4xl md:text-5xl mt-4">{g.name}</h1>
          <p className="mt-4 text-stone-600 max-w-3xl leading-relaxed">{g.description}</p>
          <div className="text-sm text-stone-500 mt-3">Options a VidaVital Medical provider may evaluate.</div>
          {minPrice != null && (
            <div className="mt-6">
              <StartingAtPill price={minPrice} size="lg" testId={`goal-price-${g.slug}`} />
            </div>
          )}
        </div>
      </section>

      <section className="vv-container py-12">
        {g.therapies?.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {g.therapies.map((t) => <TherapyCard key={t.slug} therapy={t} />)}
          </div>
        ) : (
          <div className="text-stone-500">Content coming soon. Ask a provider during your consultation.</div>
        )}

        <div className="mt-12 rounded-2xl bg-amber-50 border border-amber-100 p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="font-display text-2xl">Want a provider's guidance?</div>
            <div className="text-sm text-stone-600 mt-1">Schedule a live consultation to review your history.</div>
          </div>
          <Link to="/request-consultation" data-testid="goal-cta-schedule" className="vv-btn-primary">Schedule Consultation</Link>
        </div>

        <div className="mt-10"><Disclaimer /></div>
      </section>
    </div>
  );
}
